import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MDBCard, MDBCardBody, MDBBtn, MDBInput, MDBRow, MDBCol, MDBIcon, MDBTextArea } from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../../utils/localizedText';
import { captureError } from '../../utils/sentryHelpers';
import { DOCUMENT_TYPES } from './SystemDocumentSettings';

const client = generateClient();

/** Empty form shape — mirrors the systemDocumentSettings GraphQL type */
const emptyForm = {
  documentType: 'invoice',
  industryId: '',
  name: '',
  companyName: '',
  website: '',
  taxid: '',
  dueDays: 14,
  prefix: '',
  digits: 5,
  lastNumber: 0,
  companyAddress: { street: '', streetNumber: '', street2: '', zip: '', city: '', countryCode: 'DE' },
  companyContact: { phone: '', mobile: '', fax: '', email: '' },
  bank: { accountHolder: '', iban: '', bic: '', bankName: '' },
  legalNotice: { short: '', html: '' },
};

/** Strips GraphQL-only fields and empties before sending as a mutation input */
const toInput = (form) => {
  const { createdAt, updatedAt, __typename, ...rest } = form;
  return rest;
};

export default function EditSystemDocumentSettings() {
  // Both params present => edit mode; absent => create mode
  const { documentType: paramDocType, industryId: paramIndustryId } = useParams();
  const isEdit = Boolean(paramDocType && paramIndustryId);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState(emptyForm);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchIndustries();
    if (isEdit) fetchSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIndustries = async () => {
    try {
      const result = await client.graphql({
        query: `
          query ListIndustries {
            listIndustries {
              items { id name { language text } }
            }
          }
        `
      });
      setIndustries(result.data.listIndustries.items);
    } catch (error) {
      console.error('Error fetching industries:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: 'fetch-industries'
      });
    }
  };

  const fetchSetting = async () => {
    try {
      const result = await client.graphql({
        query: `
          query GetSystemDocumentSettings($documentType: String!, $industryId: String!) {
            getSystemDocumentSettings(documentType: $documentType, industryId: $industryId) {
              documentType
              industryId
              name
              companyName
              website
              taxid
              dueDays
              prefix
              digits
              lastNumber
              companyAddress { street streetNumber street2 zip city countryCode }
              companyContact { phone mobile fax email }
              bank { accountHolder iban bic bankName }
              legalNotice { short html }
            }
          }
        `,
        variables: { documentType: paramDocType, industryId: paramIndustryId }
      });
      const data = result.data.getSystemDocumentSettings;
      if (data) {
        // Merge into emptyForm so nested objects always exist for controlled inputs
        setFormData({
          ...emptyForm,
          ...data,
          companyAddress: { ...emptyForm.companyAddress, ...(data.companyAddress || {}) },
          companyContact: { ...emptyForm.companyContact, ...(data.companyContact || {}) },
          bank: { ...emptyForm.bank, ...(data.bank || {}) },
          legalNotice: { ...emptyForm.legalNotice, ...(data.legalNotice || {}) },
        });
      }
    } catch (error) {
      console.error('Error fetching document setting:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: 'fetch-document-settings',
        context: { documentType: paramDocType, industryId: paramIndustryId }
      });
    }
  };

  /** Updates a top-level field */
  const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  /** Updates a field inside a nested object (companyAddress, bank, ...) */
  const setNested = (group, field, value) =>
    setFormData((prev) => ({ ...prev, [group]: { ...prev[group], [field]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const input = toInput(formData);
      const mutation = isEdit
        ? `mutation UpdateSystemDocumentSettings($input: UpdateSystemDocumentSettingsInput!) {
             updateSystemDocumentSettings(input: $input) { documentType industryId }
           }`
        : `mutation CreateSystemDocumentSettings($input: CreateSystemDocumentSettingsInput!) {
             createSystemDocumentSettings(input: $input) { documentType industryId }
           }`;
      await client.graphql({ query: mutation, variables: { input } });
      navigate('/admin/system/document-settings');
    } catch (error) {
      console.error('Error saving document setting:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: isEdit ? 'update-document-settings' : 'create-document-settings',
        context: { documentType: formData.documentType, industryId: formData.industryId }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBCard>
      <MDBCardBody>
        <h5 className="mb-4">
          {isEdit ? t('Edit Document Setting') : t('Create Document Setting')}
        </h5>
        <form onSubmit={handleSubmit}>
          {/* Identity — part of the composite primary key, locked when editing */}
          <h6 className="text-muted mb-3">{t('Identification')}</h6>
          <MDBRow>
            <MDBCol md="6">
              <label className="form-label">{t('Document Type')}</label>
              <select
                className="form-select"
                value={formData.documentType}
                onChange={(e) => setField('documentType', e.target.value)}
                disabled={isEdit}
                required
              >
                {DOCUMENT_TYPES.map((dt) => (
                  <option key={dt} value={dt}>{dt}</option>
                ))}
              </select>
            </MDBCol>
            <MDBCol md="6">
              <label className="form-label">{t('Industry')}</label>
              <select
                className="form-select"
                value={formData.industryId}
                onChange={(e) => setField('industryId', e.target.value)}
                disabled={isEdit}
                required
              >
                <option value="">{t('Please select')}</option>
                {industries.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {getLocalizedText(ind.name)} ({ind.id})
                  </option>
                ))}
              </select>
            </MDBCol>
          </MDBRow>

          {/* Company details */}
          <h6 className="text-muted mb-3 mt-4">{t('Company')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="6">
              <MDBInput
                label={t('Name')}
                value={formData.name || ''}
                onChange={(e) => setField('name', e.target.value)}
                required
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Company Name')}
                value={formData.companyName || ''}
                onChange={(e) => setField('companyName', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Website')}
                value={formData.website || ''}
                onChange={(e) => setField('website', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Tax ID')}
                value={formData.taxid || ''}
                onChange={(e) => setField('taxid', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          {/* Address */}
          <h6 className="text-muted mb-3 mt-4">{t('Address')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="8">
              <MDBInput
                label={t('Street')}
                value={formData.companyAddress.street || ''}
                onChange={(e) => setNested('companyAddress', 'street', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="4">
              <MDBInput
                label={t('Street Number')}
                value={formData.companyAddress.streetNumber || ''}
                onChange={(e) => setNested('companyAddress', 'streetNumber', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="12">
              <MDBInput
                label={t('Address Line 2')}
                value={formData.companyAddress.street2 || ''}
                onChange={(e) => setNested('companyAddress', 'street2', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label={t('ZIP')}
                value={formData.companyAddress.zip || ''}
                onChange={(e) => setNested('companyAddress', 'zip', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('City')}
                value={formData.companyAddress.city || ''}
                onChange={(e) => setNested('companyAddress', 'city', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label={t('Country Code')}
                value={formData.companyAddress.countryCode || ''}
                onChange={(e) => setNested('companyAddress', 'countryCode', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          {/* Contact */}
          <h6 className="text-muted mb-3 mt-4">{t('Contact')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="6">
              <MDBInput
                label={t('Email')}
                type="email"
                value={formData.companyContact.email || ''}
                onChange={(e) => setNested('companyContact', 'email', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Phone')}
                value={formData.companyContact.phone || ''}
                onChange={(e) => setNested('companyContact', 'phone', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Mobile')}
                value={formData.companyContact.mobile || ''}
                onChange={(e) => setNested('companyContact', 'mobile', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Fax')}
                value={formData.companyContact.fax || ''}
                onChange={(e) => setNested('companyContact', 'fax', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          {/* Bank */}
          <h6 className="text-muted mb-3 mt-4">{t('Bank Account')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="6">
              <MDBInput
                label={t('Account Holder')}
                value={formData.bank.accountHolder || ''}
                onChange={(e) => setNested('bank', 'accountHolder', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('Bank Name')}
                value={formData.bank.bankName || ''}
                onChange={(e) => setNested('bank', 'bankName', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('IBAN')}
                value={formData.bank.iban || ''}
                onChange={(e) => setNested('bank', 'iban', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label={t('BIC')}
                value={formData.bank.bic || ''}
                onChange={(e) => setNested('bank', 'bic', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          {/* Document numbering & terms */}
          <h6 className="text-muted mb-3 mt-4">{t('Numbering & Terms')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="3">
              <MDBInput
                label={t('Prefix')}
                value={formData.prefix || ''}
                onChange={(e) => setField('prefix', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                type="number"
                label={t('Digits')}
                value={formData.digits ?? ''}
                onChange={(e) => setField('digits', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                type="number"
                label={t('Last Number')}
                value={formData.lastNumber ?? ''}
                onChange={(e) => setField('lastNumber', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                type="number"
                label={t('Due Days')}
                value={formData.dueDays ?? ''}
                onChange={(e) => setField('dueDays', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              />
            </MDBCol>
          </MDBRow>

          {/* Legal notice */}
          <h6 className="text-muted mb-3 mt-4">{t('Legal Notice')}</h6>
          <MDBRow className="g-3">
            <MDBCol md="12">
              <MDBTextArea
                label={t('Short')}
                rows={2}
                value={formData.legalNotice.short || ''}
                onChange={(e) => setNested('legalNotice', 'short', e.target.value)}
              />
            </MDBCol>
            <MDBCol md="12">
              <MDBTextArea
                label={t('HTML')}
                rows={4}
                value={formData.legalNotice.html || ''}
                onChange={(e) => setNested('legalNotice', 'html', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          <div className="d-flex justify-content-end mt-4">
            <MDBBtn
              type="button"
              color="secondary"
              className="me-2"
              onClick={() => navigate('/admin/system/document-settings')}
            >
              {t('Cancel')}
            </MDBBtn>
            <MDBBtn type="submit" disabled={loading}>
              {loading && <MDBIcon icon="spinner" spin className="me-2" />}
              {t('Save')}
            </MDBBtn>
          </div>
        </form>
      </MDBCardBody>
    </MDBCard>
  );
}
